import './index.css'

import clsx from 'clsx'
import isEmpty from 'lodash/isEmpty'
import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { SHOWCASES } from '@disco/constants/FeatureTypes'
import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import { updateShortlist } from '@disco/state-management/actions/ShortlistActions'
import { isDeviceTypePhone } from '@disco/state-management/selectors/app'
import { useFeatureEnabled } from '@disco/state-management/selectors/business'
import { getLocationBeforeTransitions } from '@disco/state-management/selectors/routing'
import { getShortlistById } from '@disco/state-management/selectors/shortlists'
import {
  vimeoIdRegExpString,
  youTubeIdRegExpString,
} from '@disco/utils/getVideoEmbedURL'
import { getShortlistPresentationType } from '@disco/utils/shortlist'
import { getTrackSubheader } from '@disco/utils/tracks'

import { getShortlistBaseRoute } from 'utils/routing'

import DiscoVideoSelection from 'components/common/disco-video-selection'
import Row from 'components/common/row'
import TrackInfo from 'components/common/track-info'
import ImageRecommendations from 'components/modals/image-recommendations-modal'
import ImageRecommendationsButton from 'components/modals/image-recommendations-modal/button'
import Alert from 'components/ui/alert'
import DatePicker from 'components/ui/date-picker'
import CustomDropdown from 'components/ui/dropdowns/custom-dropdown'
import ExpandableList from 'components/ui/expandable-list'
import Heading from 'components/ui/heading'
import HTMLEditor from 'components/ui/html-editor'
import Label from 'components/ui/label'
import PaneItem from 'components/ui/pane-item'
import { RadioButton } from 'components/ui/radio'
import Stack from 'components/ui/stack'
import TextField from 'components/ui/text-field'
import getTrackStatus from 'components/utils/track-status'

import { useDispatch, useSelector } from 'hooks/use-redux'

import ArtistSelection from './artist-selection'
import ImageSelector from './image-selector'

const MAX_DISPLAY_ITEMS = 3
const videoOptions = [
  {
    label: 'YouTube',
    value: 'youtube',
  },
  {
    label: 'Vimeo',
    value: 'vimeo',
  },
  {
    label: 'DISCO',
    value: 'disco',
  },
]
const DEFAULT_VIDEO_TYPE = 'disco'

const getMessageState = showcase => {
  let state

  if (typeof showcase?.artist_override_bio_html === 'string') {
    state = {
      editor: showcase?.artist_override_bio_editor,
      html: showcase?.artist_override_bio_html,
    }
  }

  if (!state && typeof showcase?.artist?.bio_html !== 'undefined') {
    state = {
      editor: showcase?.artist?.bio_editor || '',
      html: showcase?.artist?.bio_html,
    }
  }

  return state
}

type Props = {
  shortlistId: string
}

const ChooseDesign = ({ shortlistId }: Props): React.ReactElement<'div'> => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const isPhone = useSelector(isDeviceTypePhone)
  const shortlist = useSelector(state =>
    getShortlistById(state, {
      id: shortlistId,
    }),
  )
  const { showcase } = shortlist
  const videoSelected = showcase?.videos ? showcase?.videos[0] : null
  const videoTypeSelected = videoSelected?.type
  const videoTrack = videoSelected?.track
  const videoValue = videoTrack?.id
    ? { label: videoTrack.name ?? '', value: videoTrack.id }
    : null
  const showcasesEnabled = useFeatureEnabled(SHOWCASES)
  const location = useSelector(getLocationBeforeTransitions)
  const [showImageRecommendations, setShowImageRecommendations] =
    useState(false)

  const onChangeShowcase = updateObject => {
    let playlist_type = shortlist.playlist_type

    if (updateObject.hasOwnProperty('type')) {
      if (updateObject.type === 'album') {
        playlist_type = 'album'
      } else {
        playlist_type = 'playlist'
      }
    }

    dispatch(
      updateShortlist(shortlistId, {
        showcase: { ...showcase, ...updateObject },
        playlist_type,
      }),
    )
  }

  const onChangeVideo = (updateObject: {
    type?: string
    url?: string
    track?: {
      id: string
      name?: string
    }
  }) => {
    dispatch(
      updateShortlist(shortlistId, {
        showcase: { ...showcase, videos: [updateObject] },
      }),
    )
  }

  const showArtistImageAlert = !showcase?.artist?.image_url
  const showArtistBioAlert = isEmpty(showcase?.artist?.bio_editor)
  useEffect(() => {
    let updateObject = {
      title: shortlist.title,
      showcase: {
        ...showcase,
        color_theme: showcase?.color_theme || 'dark',
        updateArtistBio: showArtistBioAlert,
        updateArtistImage: showArtistImageAlert,
      },
    }

    if (Boolean(shortlist.playlist_id)) {
      updateObject.showcase.type = showcase?.type
    }

    if (!shortlist.title && showcase?.type === 'artist') {
      updateObject.title = showcase?.artist?.name || null
    }

    if (!videoTypeSelected) {
      updateObject.showcase = {
        ...updateObject.showcase,
        videos: [
          {
            type: DEFAULT_VIDEO_TYPE,
          },
        ],
      }
    }

    dispatch(updateShortlist(shortlistId, updateObject))
  }, [showcase?.type, showcase?.artist?.name])
  useEffect(() => {
    if (
      showArtistBioAlert !== showcase?.updateArtistBio ||
      showArtistImageAlert !== showcase?.updateArtistImage
    ) {
      onChangeShowcase({
        updateArtistBio: showArtistBioAlert,
        updateArtistImage: showArtistImageAlert,
      })
    }
  }, [showcase?.artist_override_image])
  useEffect(() => {
    const newMessageState = getMessageState(showcase)
    setMessageState(newMessageState)
    onChangeShowcase({
      artist_override_bio_editor: newMessageState?.editor,
      artist_override_bio_html: newMessageState?.html,
    })
  }, [showcase?.artist?.id])
  const [messageState, setMessageState] = useState(getMessageState(showcase))
  const presentationType = getShortlistPresentationType(shortlist)

  const radioButtons = [
    <div key="1">
      <div className="ChooseDesign_RadioButtonContainer">
        <RadioButton
          label="Default playlist"
          onChange={() =>
            onChangeShowcase({
              type: null,
            })
          }
          checked={showcase === null || !showcase?.type}
        />
      </div>
    </div>,
    <div key="2">
      <div className="ChooseDesign_RadioButtonContainer">
        <RadioButton
          label="Artist Page"
          onChange={() =>
            onChangeShowcase({
              type: 'artist',
            })
          }
          checked={showcase?.type === 'artist'}
          disabled={isPhone}
        />
      </div>
    </div>,
    <div key="3">
      <div className="ChooseDesign_RadioButtonContainer">
        <RadioButton
          label="Album Page"
          onChange={() =>
            onChangeShowcase({
              type: 'album',
            })
          }
          checked={showcase?.type === 'album'}
          disabled={isPhone}
        />
      </div>
    </div>,
  ]
  return (
    <div className="ChooseDesign">
      <div className="ChooseDesign_Spacer">
        <Heading size={400}>Choose a design</Heading>
      </div>
      {!isPhone ? (
        <Row columnsSize={[4, 4, 4]} marginBottom="small">
          {radioButtons}
        </Row>
      ) : (
        <Stack space="small">
          {radioButtons} <div />
        </Stack>
      )}
      {showcase?.type && !isPhone && (
        <>
          {showcase?.type === 'album' && (
            <>
              <Row columnsSize={[12]}>
                <div>
                  <p className="ChooseDesign_Text">
                    Create an album page to showcase your album with an artist
                    image, bio/release details, videos, social links and more.{' '}
                    <a
                      className="text-link"
                      href="https://support.disco.ac/home/albums-pages-guide"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Find out more.
                    </a>
                  </p>
                </div>
              </Row>
              <Row columnsSize={[12]} marginBottom="small">
                <div>
                  <div className="ChooseDesign_ReleaseInfoWrapper">
                    <div className="ChooseDesign_ArtworkContainer">
                      <Label caption="Album Artwork" />
                      <ImageSelector
                        type="album"
                        shortlist={shortlist}
                        onChangeShowcase={onChangeShowcase}
                        disabled={!showcasesEnabled}
                      />
                    </div>
                    <div className="ChooseDesign_ReleaseInfoContainer">
                      <Stack space="xsmall">
                        <TextField
                          label="Release Name (Playlist Title)"
                          value={shortlist.title}
                          onChange={event => {
                            dispatch(
                              updateShortlist(shortlistId, {
                                title: event.target.value,
                              }),
                            )
                          }}
                          disabled={!showcasesEnabled}
                        />
                        <div>
                          <Label caption="Release Date" />
                          <DatePicker
                            value={shortlist.release_date}
                            onChange={value => {
                              dispatch(
                                updateShortlist(shortlistId, {
                                  release_date: value,
                                }),
                              )
                            }}
                          />
                        </div>
                      </Stack>
                    </div>
                  </div>
                </div>
              </Row>
            </>
          )}

          {showcase?.type === 'artist' && (
            <Row columnsSize={[12]}>
              <div>
                <p className="ChooseDesign_Text">
                  Create an artist page to showcase your artist with an artist
                  image, bio/release details, videos, social links and more.{' '}
                  <a
                    className="text-link"
                    href="https://support.disco.ac/home/albums-pages-guide"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Find out more.
                  </a>
                </p>
              </div>
            </Row>
          )}
          <Row columnsSize={[12]}>
            <div>
              <ArtistSelection
                shortlistId={shortlistId}
                disabled={!showcasesEnabled}
                resetBio={() => setMessageState(null)}
              />

              {(() => {
                const baseRoute = getShortlistBaseRoute(location)
                return (
                  <Link
                    className={clsx('ChooseDesign_Text text-link', {
                      'text-link--disabled': !showcasesEnabled,
                    })}
                    to={`${baseRoute}/shortlists/${shortlistId}/save/presentation/find-artist`}
                    onClick={() => {
                      dispatch(
                        sendMixpanelEvent(
                          `${presentationType} - Can't find an artist`,
                        ),
                      )
                    }}
                  >
                    Can’t find an artist?
                  </Link>
                )
              })()}
            </div>
          </Row>
          <Row columnsSize={[12]}>
            <div>
              <div className="ChooseDesign_Spacer">
                <Heading size={400}>Color theme</Heading>
              </div>
              <div className="ChooseDesign_ThemeContainer">
                <div className="ChooseDesign_Theme">
                  <RadioButton
                    label="Dark"
                    onChange={() =>
                      onChangeShowcase({
                        color_theme: 'dark',
                      })
                    }
                    checked={showcase?.color_theme === 'dark'}
                    disabled={!showcasesEnabled}
                  />
                </div>
                <div className="ChooseDesign_Theme">
                  <RadioButton
                    label="Light"
                    onChange={() =>
                      onChangeShowcase({
                        color_theme: 'light',
                      })
                    }
                    checked={showcase?.color_theme === 'light'}
                    disabled={!showcasesEnabled}
                  />
                </div>
              </div>
            </div>
          </Row>
          <Row columnsSize={[12]}>
            <div>
              <div className="ChooseDesign_Spacer">
                <Heading size={400}>Hero info</Heading>
              </div>
              {showArtistImageAlert && (
                <>
                  <Alert
                    status="change"
                    dismissible
                    id="choose_design_artist_image"
                  >
                    Editing this image will update the artist profile
                    everywhere.
                  </Alert>
                  <div className="ChooseDesign_AlertSpacer" />
                </>
              )}

              <Label caption="Artist image">
                <ImageRecommendationsButton
                  onClick={() => setShowImageRecommendations(true)}
                  disabled={!showcasesEnabled}
                />
              </Label>
              <ImageSelector
                type="artist"
                shortlist={shortlist}
                onChangeShowcase={onChangeShowcase}
                disabled={!showcasesEnabled}
              />
            </div>
          </Row>
          <Row columnsSize={[12]}>
            <div>
              {messageState && showArtistBioAlert && (
                <>
                  <Alert
                    status="change"
                    dismissible
                    id="choose_design_artist_bio"
                  >
                    Editing this bio will update the artist profile everywhere.
                  </Alert>
                  <div className="ChooseDesign_AlertSpacer" />
                </>
              )}
              {messageState && (
                <HTMLEditor
                  key={`${showcase?.artist?.id}-${showcase?.type}`}
                  disabled={!showcasesEnabled}
                  initialContent={messageState?.editor}
                  label="Artist / Album Info"
                  placeholder="Enter information about the artist and album"
                  onBlur={() => {
                    if (!showcasesEnabled) return
                    dispatch(
                      sendMixpanelEvent(
                        `${presentationType} - Edit artist/album info`,
                      ),
                    )

                    if (showArtistBioAlert) {
                      onChangeShowcase({
                        artist: {
                          ...showcase?.artist,
                          updatedFields: {
                            ...(showcase?.artist?.updatedFields || {}),
                            bio_editor: messageState?.editor,
                            bio_html: messageState?.html,
                          },
                        },
                      })
                    }

                    onChangeShowcase({
                      artist_override_bio_editor: messageState?.editor,
                      artist_override_bio_html: messageState?.html,
                    })
                  }}
                  onChange={(editor, html) => {
                    setMessageState({
                      editor,
                      html,
                    })
                  }}
                />
              )}
            </div>
          </Row>
          <Row columnsSize={[12]}>
            <div>
              <div className="ChooseDesign_Spacer">
                <Heading size={400}>Social Links</Heading>
              </div>
              <div className="ChooseDesign_Spacer">
                {(() => {
                  const baseRoute = getShortlistBaseRoute(location)
                  return showcasesEnabled ? (
                    <Link
                      className={clsx('text-link')}
                      to={`${baseRoute}/shortlists/${shortlistId}/save/presentation/social-links`}
                      onClick={() => {
                        dispatch(
                          sendMixpanelEvent(
                            `${presentationType} - Manage social links`,
                          ),
                        )
                      }}
                    >
                      Manage social links
                    </Link>
                  ) : (
                    <span className={clsx('text-link', 'text-link--disabled')}>
                      Manage social links
                    </span>
                  )
                })()}
              </div>
              <Alert
                status="change"
                dismissible
                id="choose_design_artist_links"
              >
                Editing the social links will update the artist profile
                everywhere.
              </Alert>
            </div>
          </Row>
          <Row columnsSize={[12]}>
            <div>
              <div className="ChooseDesign_Spacer">
                <Heading size={400}>Contents</Heading>
              </div>
              <div className="ChooseDesign_Spacer">
                <ExpandableList
                  limit={MAX_DISPLAY_ITEMS}
                  renderButton={button => {
                    return (
                      <>
                        <div className="ChooseDesign_Spacer" />
                        {button}
                      </>
                    )
                  }}
                >
                  {shortlist.items.map(({ track }) => {
                    return (
                      <PaneItem
                        key={track.id}
                        content={
                          <TrackInfo location="explorer" track={track} />
                        }
                        appearance="default"
                        height="default"
                        title={track.name ? getTrackStatus(track).line1 : ''}
                        caption={track.name ? getTrackSubheader(track) : null}
                      />
                    )
                  })}
                </ExpandableList>
              </div>
            </div>
          </Row>

          <div className="ChooseDesign_Spacer">
            <Heading size={400}>Video</Heading>
          </div>
          <Row columnsSize={[3, 9]}>
            <div>
              <CustomDropdown
                options={videoOptions}
                value={videoTypeSelected}
                placeholder={null}
                onChange={value => {
                  dispatch(
                    sendMixpanelEvent(
                      `${presentationType} - Add video ${value?.label}`,
                    ),
                  )
                  onChangeVideo({
                    type: value?.value,
                  })
                }}
                disabled={!showcasesEnabled}
              />
            </div>
            <div>
              {videoTypeSelected === DEFAULT_VIDEO_TYPE ? (
                <DiscoVideoSelection
                  value={videoValue}
                  onChange={value =>
                    onChangeVideo({
                      track: {
                        id: value?.value,
                        name: value?.label,
                      },
                      type: videoTypeSelected,
                    })
                  }
                  disabled={!showcasesEnabled}
                />
              ) : (
                <TextField
                  placeholder={`Enter ${
                    videoOptions.find(
                      ({ value }) => videoTypeSelected === value,
                    )?.label || ''
                  } URL`}
                  value={videoSelected?.url || ''}
                  onChange={event => {
                    onChangeVideo({
                      url: event.target.value,
                      type: videoTypeSelected,
                    })
                  }}
                  disabled={!showcasesEnabled}
                  pattern={
                    videoTypeSelected === 'vimeo'
                      ? vimeoIdRegExpString
                      : youTubeIdRegExpString
                  }
                  patternErrorMessage={
                    videoTypeSelected === 'vimeo'
                      ? 'Please enter a vimeo URL for a single video.'
                      : 'Please enter a YouTube URL for a single video.'
                  }
                  onInvalid={() => {
                    const baseRoute = getShortlistBaseRoute(location)
                    navigate(
                      `${baseRoute}/shortlists/${shortlist.id}/save/presentation`,
                    )
                  }}
                />
              )}
            </div>
          </Row>
        </>
      )}
      {showcase?.type && isPhone && (
        <Stack space="small">
          <Alert status="info" dismissible id="choose_design_phone">
            Artist and album Pages can’t be edited on mobile web. To set up your
            Page, open this playlist on desktop.
          </Alert>
          <div />
        </Stack>
      )}
      {showImageRecommendations && (
        <ImageRecommendations
          onClose={() => setShowImageRecommendations(false)}
        />
      )}
    </div>
  )
}

export default ChooseDesign
